import { Check, Lock } from 'lucide-react';

import { useLanguage } from '@/components/LanguageContext';
import { PERMISOS_SCREENSHOT } from '@/lib/googleConnectGuide';

// Centro de la casilla dentro de la captura (475×127 px nativos), en % para
// que el resaltado siga encima al escalar la imagen. Si se cambia la captura,
// hay que volver a medir esto.
const CASILLA = { left: '95.5%', top: '56.5%' };

/**
 * La captura de la casilla de permisos de Google, VESTIDA para que no parezca
 * parte de nuestra UI.
 *
 * Sola, la captura era un recorte limpio con un cuadrado vacío del tamaño de
 * una casilla de verdad, en mitad de nuestro modal: la gente intentaba
 * marcarla AQUÍ. Tres cosas lo evitan:
 *   - el marco de navegador con `accounts.google.com`, que la saca de la app;
 *   - la casilla marcándose sola en bucle, que se lee como «esto es lo que
 *     harás allí» y no como un control;
 *   - el rótulo «Así lo verás en Google» y `pointer-events-none`, para que
 *     ni el cursor sugiera que se puede pulsar.
 *
 * La usan los dos modales previos al OAuth y el banner ámbar de
 * `ConnectionList`.
 *
 * `destacada` la mete en un recuadro ámbar para que sea lo primero que se ve
 * del modal. El banner de `ConnectionList` la monta sin él: ese banner ya es
 * ámbar, y un recuadro ámbar dentro de otro no destaca nada.
 */
/** El aro que late y la casilla marcándose, ya centrados por quien los monta.
 * Los comparten la captura y la réplica en HTML. Con movimiento reducido la
 * casilla se queda marcada. */
function CasillaAnimada({ aro, casilla, style }) {
  return (
    <>
      <span
        aria-hidden="true"
        style={style}
        className={`absolute ${aro} aspect-square -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-[#1a73e8] animate-casilla-pulso motion-reduce:animate-none`}
      />
      <span
        aria-hidden="true"
        style={style}
        className={`absolute flex ${casilla} aspect-square -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-[2px] bg-[#1a73e8] animate-casilla-check motion-reduce:animate-none`}
      >
        <Check className="h-[80%] w-[80%] text-white" strokeWidth={3.5} />
      </span>
    </>
  );
}

/**
 * La misma pantalla de Google, en portugués y en HTML en vez de una captura.
 *
 * Google pinta su pantalla de consentimiento en el idioma de la CUENTA, así
 * que a un cliente portugués la captura en español le enseñaba algo que no iba
 * a ver. Como la pantalla es solo texto, un punto, un enlace y una casilla, se
 * replica en HTML sin depender de una captura por idioma.
 *
 * Los textos son los LITERALES de Google, no traducciones nuestras: por eso no
 * van en `locales/`. Si Google cambia su redacción, se cambian aquí.
 *
 * La animación se ancla a la casilla de verdad, no a porcentajes: la réplica
 * se reajusta al ancho y la casilla no está en un sitio fijo. El `pb-6` deja
 * sitio al rótulo «Marca-a», anclado abajo a la derecha: sin él taparía la
 * propia casilla.
 */
function PantallaGooglePt({ alt }) {
  return (
    <div
      role="img"
      aria-label={alt}
      data-testid="captura-permisos-html"
      className="px-4 pt-3 pb-6 font-['Roboto',Arial,sans-serif]"
    >
      <p className="text-[19px] leading-snug text-[#202124]">
        Selecione aquilo a que a <span className="text-[#1a73e8]">MyTapstar</span> pode aceder
      </p>
      <div className="flex items-start gap-3 border-b border-[#dadce0] pb-3 pt-4">
        <span className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-[#1a73e8]" />
        <div className="min-w-0 flex-1 text-[14px] leading-snug text-[#3c4043]">
          <p>Ver, editar, criar e eliminar as suas fichas da empresa.</p>
          <p className="font-medium text-[#1a73e8]">Ver detalhes do acesso</p>
        </div>
        <span className="relative mt-0.5 flex-shrink-0">
          <span className="block h-[18px] w-[18px] rounded-[2px] border-2 border-[#5f6368]" />
          <CasillaAnimada
            aro="w-9"
            casilla="w-[18px]"
            style={{ left: '50%', top: '50%' }}
          />
        </span>
      </div>
    </div>
  );
}

export default function CapturaPermisosGoogle({ destacada = false, className = '' }) {
  const { t, language } = useLanguage();
  const alt = t('gbusiness.errors.oauth_insufficient_scope_alt');
  const marco = destacada
    ? 'space-y-2 rounded-lg border border-amber-200 bg-amber-50 p-3'
    : 'space-y-1.5';
  return (
    <figure className={`${marco} ${className}`}>
      <figcaption
        className={destacada
          ? 'text-sm font-semibold text-amber-900'
          : 'text-xs font-medium text-gray-500'}
      >
        {t('gbusiness.capturaPermisos.rotulo')}
      </figcaption>
      <div className="pointer-events-none select-none max-w-[475px] overflow-hidden rounded-lg border border-gray-300 bg-white shadow-sm">
        <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-100 px-2.5 py-1.5" aria-hidden="true">
          <span className="flex gap-1">
            <span className="h-2 w-2 rounded-full bg-gray-300" />
            <span className="h-2 w-2 rounded-full bg-gray-300" />
            <span className="h-2 w-2 rounded-full bg-gray-300" />
          </span>
          <span className="flex min-w-0 flex-1 items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[10px] text-gray-500">
            <Lock className="h-2.5 w-2.5 flex-shrink-0" />
            <span className="truncate">accounts.google.com</span>
          </span>
        </div>
        <div className="relative">
          {language === 'pt' ? (
            <PantallaGooglePt alt={alt} />
          ) : (
            <>
              <img
                src={PERMISOS_SCREENSHOT}
                alt={alt}
                loading="lazy"
                width={475}
                height={127}
                className="block w-full"
              />
              <CasillaAnimada aro="w-[9%]" casilla="w-[4.2%]" style={CASILLA} />
            </>
          )}
          {/* La flecha va DETRÁS del texto: el rótulo está anclado abajo a la
              derecha, justo debajo de la casilla, así que al final es donde
              apunta a ella y no al vacío de su izquierda. */}
          <span
            aria-hidden="true"
            className="absolute bottom-1 right-1.5 rounded bg-[#1a73e8] px-1.5 py-0.5 text-[10px] font-semibold leading-none text-white"
          >
            {t('gbusiness.capturaPermisos.marcala')} ↑
          </span>
        </div>
      </div>
    </figure>
  );
}
